/** The artwork behind the measurement-kind glyphs, as plain data.
 *
 *  The glyphs are drawn in two places that cannot share a renderer: React SVG
 *  in the toolbar and the Types panel, and pdf-lib paths in the exported sheet's
 *  schedule. They were separately authored, so the export showed lookalikes — a
 *  three-point chevron where the app draws a four-point zigzag, a plain
 *  rectangle where the app draws a pentagon. Close enough to look intentional,
 *  different enough that the legend on an issued sheet did not match the icons
 *  the estimator picked the type by.
 *
 *  Holding the geometry here, framework-free, is what stops that drifting again.
 *  React components live in measurementGlyphs.tsx; the PDF side reads the same
 *  constants.
 *
 *  Everything is on a 24 x 24 grid with y DOWNWARD, which is both the SVG
 *  viewBox convention and what pdf-lib's drawSvgPath expects relative to its
 *  anchor — so neither consumer has to flip anything.
 */

/** The grid every path below is drawn on. Scale by size / GLYPH_GRID. */
export const GLYPH_GRID = 24;

/** Linear: an open polyline. The measurement is the line's run, so the glyph is
 *  a line rather than a node graph. Stroked, never filled. */
export const LINEAR_GLYPH_PATH = 'M 3 19 L 8 10 L 14 15 L 20 6';
export const LINEAR_GLYPH_STROKE = 2.2;

/** Area: a closed plot outline, lightly filled so it reads as an extent rather
 *  than a route. */
export const AREA_GLYPH_PATH =
  'M19.87,8.69,13.79,4A2.73,2.73,0,0,0,12,3.25,2.73,2.73,0,0,0,10.21,4L4.13,8.69A2.19,'
  + '2.19,0,0,0,3.37,11c.17.6,2.42,7.8,2.6,8.27a2.15,2.15,0,0,0,2.14,1.45H15.9A2.15,'
  + '2.15,0,0,0,18,19.3c.19-.48,2.43-7.68,2.6-8.27A2.17,2.17,0,0,0,19.87,8.69Z';
export const AREA_GLYPH_STROKE = 1.5;
export const AREA_GLYPH_FILL_OPACITY = 0.18;

/** Volume: an isometric cube. Same treatment as the area glyph. */
export const VOLUME_GLYPH_PATH =
  'M20.72,7a.41.41,0,0,0-.07-.17.61.61,0,0,0-.07-.1.43.43,0,0,0-.12-.11l-.05,0,0,0h0s0,0,0,'
  + '0L12.4,2.79l-.08,0a.74.74,0,0,0-.72,0L3.68,6.53s0,0,0,0h0l0,0,0,0a.49.49,0,0,0-.18.2A.71.71,'
  + '0,0,0,3.28,7a.69.69,0,0,0,0,.2V17.1a.75.75,0,0,0,.45.68l7.89,3.49.11.05.14.05.16,0,.17,0,.12,'
  + '0h0l.1-.05,7.9-3.49a.75.75,0,0,0,.44-.68V7.21A.69.69,0,0,0,20.72,7ZM11.25,19.48l-6.5-2.87V8.36l6.5,'
  + '2.88ZM12,9.93,5.81,7.18,12,4.25l6.19,2.94Zm7.24,6.68-6.5,2.87V11.24l6.5-2.88Z';
export const VOLUME_GLYPH_STROKE = 1.5;
export const VOLUME_GLYPH_FILL_OPACITY = 0.15;

/** Count: loose filled dots. Solid rather than outlined — a count is a tally of
 *  discrete things, not a shape with an extent to trace.
 *
 *  Deliberately the generic kind glyph, NOT the type's own count symbol. The
 *  legend mirrors the Types panel, which shows these dots; the symbol the
 *  estimator chose is what marks the sheet itself. */
export const COUNT_GLYPH_DOTS: ReadonlyArray<{ cx: number; cy: number; r: number }> = [
  { cx: 7, cy: 15, r: 2.5 },
  { cx: 14, cy: 9, r: 2.5 },
  { cx: 18, cy: 16, r: 2.5 },
];
